import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, AlertCircle } from "lucide-react";
import { Person } from "../types";
import { maskCPF, maskPhone } from "@/utils/maskUtils";

interface LegalRepresentativeFormProps {
  person: Person;
  onChange: (updatedPerson: Person) => void;
}

const LegalRepresentativeForm: React.FC<LegalRepresentativeFormProps> = ({
  person,
  onChange,
}) => {
  const [activeRepresentative, setActiveRepresentative] = useState(0);
  const representatives = person.legalRepresentatives || [];
  
  // Estado para rastrear se o formulário foi tocado/tentativa de submissão
  const [touched, setTouched] = useState<boolean>(false);
  
  // Verificar se a pessoa tem os dados básicos preenchidos
  const isPersonValid = !!(person.name && person.email && person.cpf && person.celphone);
  
  // Verificar se o representante ativo está completamente preenchido
  const isCurrentRepresentativeValid = (): boolean => {
    if (!representatives.length) return true;
    const currentRep = representatives[activeRepresentative]?.[0];
    return !!(currentRep && currentRep.name && currentRep.email && currentRep.cpf && currentRep.celphone);
  };
  
  // Adicionar representante
  const handleAddRepresentative = () => {
    const newRepresentatives = [...representatives, [{}]];
    onChange({
      ...person,
      legalRepresentatives: newRepresentatives
    });
    // Ativar o novo representante
    setActiveRepresentative(newRepresentatives.length - 1);
  };
  
  // Remover representante
  const handleRemoveRepresentative = () => {
    if (!representatives.length) return;
    
    const newRepresentatives = representatives.filter((_, idx) => idx !== activeRepresentative);
    
    // Atualizar o estado da pessoa
    onChange({
      ...person,
      legalRepresentatives: newRepresentatives.length ? newRepresentatives : undefined
    });
    
    // Ajustar índice do representante ativo se necessário
    if (activeRepresentative >= newRepresentatives.length) {
      setActiveRepresentative(Math.max(0, newRepresentatives.length - 1));
    }
  };
  
  // Atualizar campo do representante
  const handleFieldChange = (field: string, value: string) => {
    if (!representatives.length) return;
    
    const newRepresentatives = [...representatives];
    
    // Garantir que há um objeto representante
    if (!newRepresentatives[activeRepresentative][0]) {
      newRepresentatives[activeRepresentative][0] = {};
    }
    
    // Atualizar o campo
    newRepresentatives[activeRepresentative][0] = {
      ...newRepresentatives[activeRepresentative][0],
      [field]: value
    };
    
    onChange({
      ...person,
      legalRepresentatives: newRepresentatives
    });
  };
  
  // Detectar tentativa de validação
  React.useEffect(() => {
    if (person && Object.keys(person).length > 0) {
      // Verificar se há representantes legais, mas estão incompletos
      if (person.legalRepresentatives && person.legalRepresentatives.length > 0) {
        const hasIncomplete = person.legalRepresentatives.some(repArray => {
          const rep = repArray[0] || {};
          return !rep.name || !rep.email || !rep.cpf || !rep.celphone;
        });
        if (hasIncomplete) {
          setTouched(true);
        }
      }
    }
  }, [person]);
  
  // Função para verificar se um campo está em erro
  const isFieldInError = (field: keyof Person): boolean => {
    if (!representatives[activeRepresentative] || !representatives[activeRepresentative][0]) return false;
    return touched && !representatives[activeRepresentative][0][field];
  };
  
  // Função para obter a classe de estilo do input baseada no erro
  const getInputClassName = (field: keyof Person): string => {
    const baseClass = "w-full placeholder:text-background/60 border-0 border-b rounded-none focus-visible:ring-0";
    const errorClass = isFieldInError(field) ? "border-b-red-500" : "border-b-background";
    return `${baseClass} ${errorClass}`;
  };
  
  // Se não houver representantes, mostrar apenas o botão para adicionar
  if (!representatives.length) {
    return (
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="font-bold text-background">Representante Legal</p>
          </div>
          <div 
            onClick={handleAddRepresentative}
            className="flex items-center justify-center w-[35px] h-[35px] bg-primary cursor-pointer rounded-md"
          >
            <Plus size={16} />
          </div>
        </div>
      </div>
    );
  }
  
  // Obter os dados do representante ativo
  const currentRep = representatives[activeRepresentative]?.[0] || {};
  
  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-col-reverse sm:flex-row justify-center items-center gap-1 sm:gap-4 w-full relative">
        <div className="flex gap-2">
          {representatives.map((_, index) => (
            <div 
              key={index} 
              onClick={() => setActiveRepresentative(index)} 
              className="flex flex-col justify-between items-center w-[35px] h-[49px] cursor-pointer"
            >
              <div className={`flex items-center justify-center w-[35px] h-[35px] rounded-md ${index === activeRepresentative ? 'bg-primary' : 'bg-background/10'}`}>
                <span className="text-background text-xs sm:text-base">
                  {(index + 1).toString().padStart(2, '0')}
                </span>
              </div>
              {index === activeRepresentative && (
                <span className="w-full h-[5px] rounded-xs bg-primary" />
              )}
            </div>
          ))}
        </div>
        
        <div className="flex self-end gap-2 sm:absolute right-0 top-0">
          <Button 
            onClick={handleRemoveRepresentative} 
            variant="destructive" 
            className="h-[35px] text-background rounded-md bg-background/10 hover:bg-background/10 my-0 py-0 has-[>svg]:px-2"
          >
            <Trash2 size={16} />
          </Button>
          <div 
            onClick={handleAddRepresentative}
            className="flex items-center justify-center w-[35px] h-[35px] bg-primary cursor-pointer rounded-md"
          >
            <Plus size={16} />
          </div>
        </div>
      </div>
      
      <div className="space-y-4 border-t border-background/20 pt-4">
        {/* Nome do representante legal ativo, se houver mais de um */}
        {representatives.length > 1 && (
          <p className="text-background text-sm sm:text-base pb-2">
            {`${activeRepresentative + 1}º Representante Legal`}{currentRep.name ? `: ${currentRep.name}` : ''}
          </p>
        )}
        
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              Nome do Representante{isFieldInError('name') && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input 
              className={getInputClassName('name')}
              placeholder="Digite o nome completo"
              value={currentRep.name || ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
            />
            {isFieldInError('name') && (
              <p className="text-red-500 text-xs mt-1">Nome é obrigatório</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              Email do Representante{isFieldInError('email') && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input 
              className={getInputClassName('email')}
              placeholder="exemplo@email.com"
              value={currentRep.email || ''}
              onChange={(e) => handleFieldChange('email', e.target.value)}
            />
            {isFieldInError('email') && (
              <p className="text-red-500 text-xs mt-1">Email é obrigatório</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              CPF do Representante{isFieldInError('cpf') && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input 
              className={getInputClassName('cpf')}
              placeholder="000.000.000-00"
              value={currentRep.cpf || ''}
              onChange={(e) => handleFieldChange('cpf', maskCPF(e.target.value))}
            />
            {isFieldInError('cpf') && (
              <p className="text-red-500 text-xs mt-1">CPF é obrigatório</p>
            )}
          </div>
          
          <div className="space-y-2">
            <label className="text-primary font-bold text-sm sm:text-base px-0">
              Celular do Representante{isFieldInError('celphone') && <span className="text-red-500 ml-1">*</span>}
            </label>
            <Input 
              className={getInputClassName('celphone')}
              placeholder="(00) 00000-0000"
              value={currentRep.celphone || ''}
              onChange={(e) => handleFieldChange('celphone', maskPhone(e.target.value))}
            />
            {isFieldInError('celphone') && (
              <p className="text-red-500 text-xs mt-1">Celular é obrigatório</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalRepresentativeForm; 